<template>
    <div class="relative mb-4 flex flex-col gap-2">
        <vx-box
            class="relative flex p-4 gap-2 items-start flex-wrap lg:flex-nowrap lg:py-3 lg:pl-6 lg:pr-3">
            <div class="w-[100px] shrink-0 order-1 pt-2">
                <font-awesome-icon widthAuto :icon="faShareNodes"
                    class="mr-2 text-sky-500"></font-awesome-icon>
                <span class="whitespace-nowrap text-slate-800 text-base font-extrabold">
                    {{ props.filterIndex === 0 ? 'If' : 'And If' }}
                </span>
            </div>
            <div class="grow w-full flex flex-col gap-2 order-2 lg:order-1 lg:grow-0">
                <div data-test="attribute-name-wrapper"
                    :class="customAttributeKeyAndValue() ? 'col-span-12' : 'col-span-7'">
                    <vx-selectable placeholder="attribute name"
                        v-model="filter.attribute"
                        :options="[
                            {
                                value: 'custom_attribute',
                                label: 'group has a custom attribute with key and value',
                            },
                            {
                                value: 'group_id',
                                label: 'group',
                            },
                        ]"
                        :reduce="option => option.value"
                        :clearable="false"
                        :error="displayFirstError(validate.attribute)">

                    </vx-selectable>
                </div>

                <div v-if="!customAttributeKeyAndValue() && filter.attribute">
                    <vx-selectable placeholder="operator"
                        data-test="operator-select"
                        v-model="filter.operator"
                        :reduce="option => option.value"
                        :options="operatorOptions"
                        :clearable="false"
                        :error="displayFirstError(validate.operator)">
                    </vx-selectable>
                </div>

                <div v-if="!customAttributeKeyAndValue() && filter.attribute">
                    <group-collection-filter-values v-model="filter.values"
                        :orgUnitId="props.orgUnitId"
                        :groupSelect="filter.attribute === 'group_id'"
                    ></group-collection-filter-values>
                </div>

            </div>
            <div class="flex ml-auto order-1 lg:order-2">
                <vx-button @click.prevent="emit('remove')"
                    type="button"
                    size="md"
                    color="muted-light"
                    data-test="remove-filter">
                    <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                </vx-button>
            </div>
        </vx-box>

        <vx-box v-if="customAttributeKeyAndValue()"
            data-test="custom-attribute-key-and-value-wrapper"
            class="relative p-4 ml-10 lg:ml-[122px] lg:p-6">
            <line-nested></line-nested>

            <div class="font-extrabold text-slate-800 mb-4">Custom Attribute</div>
            <div class="flex flex-col gap-2">
                <div>
                    <vx-input data-test="custom-attribute-key"
                        v-model="filter.key"
                        placeholder="custom attribute key"
                        :error="displayFirstError(validate.key)"></vx-input>
                </div>

                <div>
                    <vx-selectable placeholder="operator"
                        data-test="operator-select"
                        v-model="filter.operator"
                        :reduce="option => option.value"
                        :options="operatorOptions"
                        :clearable="false"
                        :error="displayFirstError(validate.operator)">
                    </vx-selectable>
                </div>

                <div>
                    <group-collection-filter-values v-model="filter.values" :orgUnitId="props.orgUnitId"></group-collection-filter-values>
                </div>

            </div>
        </vx-box>
    </div>

</template>


<script setup>
import { faShareNodes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxSelectable, VxInput } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { h, watch } from 'vue';
import { csvInjection, displayFirstError, groupCustomAttributeKey } from '~/utils/validation';
import GroupCollectionFilterValues from './GroupCollectionFilterValues.vue';

const LineNested = h('div', { class: 'absolute top-9 -left-6 w-6 h-px border-solid border-y border-x-0 border-t-slate-300 border-b-white lg:-left-[72px] lg:w-[72px]' })

const operatorOptions = [
    {
        value: 'in',
        label: 'is in list',
    },
    {
        value: 'not_in',
        label: 'is not in list',
    },
]

const props = defineProps({
    filterIndex: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
    },
});

const filter = defineModel({
    type: Object,
    required: true,
});

watch(() => filter.value.attribute, () => {
    filter.value.key = '';
})

const emit = defineEmits(['remove']);

const customAttributeKeyAndValue = () => {
    return filter.value.attribute === 'custom_attribute';
}

const validate = useVuelidate({
    attribute: {
        required: helpers.withMessage('Attribute is required', required),
    },
    key: {
        requiredIf: helpers.withMessage('Key is required', requiredIf(() => customAttributeKeyAndValue())),
        maxLength: maxLength(40),
        format: groupCustomAttributeKey,
        csvInjection: csvInjection,
    },
    operator: {
        required: helpers.withMessage('Operator is required', required),
    },
}, filter);


</script>
